/* global SWDLocalise */
/* global google */
import { Loader } from '@googlemaps/js-api-loader';
import { handleResponse, getUrlParams } from '../../utils';

const key = SWDLocalise.googleMapsAPIKey;
const loader = new Loader({
    apiKey: key,
    libraries: ['places', 'geometry'],
});

const searchMap = document.querySelector('.js-map');
const searchInput = document.querySelector('.js-map-search-input');
const searchSidebar = document.querySelector('.js-map-sidebar');
const searchResults = document.querySelector('.js-map-results');

let map;
let Popup;
let svgMarker;
let isLoading = false;
const markers = [];
const popups = [];
const loaderHTML = `
    <div class="flex items-center justify-center absolute top-0 left-0 z-50 w-full h-full bg-green bg-opacity-75 js-map-loader">
        <svg class="w-24"
            viewBox="0 0 100 100">
            <path
                fill="#e0e483"
                d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50">
                <animateTransform
                    attributeName="transform"
                    attributeType="XML"
                    type="rotate"
                    dur="1s"
                    from="0 50 50"
                    to="360 50 50"
                    repeatCount="indefinite" />
            </path>
        </svg>
    </div>
`;

const disableLoader = () => {
    document.querySelectorAll('.js-map-loader').forEach((el) => el.remove());
    isLoading = false;
};

const enableLoader = () => {
    if (isLoading) return;
    searchSidebar.insertAdjacentHTML('beforeend', loaderHTML);
    isLoading = true;
};

const definePopupClass = () => {
    /**
     * A customized popup on the map.
     * @param {!google.maps.LatLng} position
     * @param {!Element} content
     * @constructor
     * @extends {google.maps.OverlayView}
     */
    Popup = function (position, content) { // eslint-disable-line func-names
        this.position = position;

        content.classList.add(
            'absolute',
            'top-0',
            'left-0',
            'p-6',
            'pb-10',
            'bg-green',
            'rounded-lg',
            'shadow',
            '-translate-x-1/2',
            '-translate-y-full',
        );

        const pixelOffset = document.createElement('div');
        pixelOffset.classList.add(
            'absolute',
            'bottom-2',
            'left-0',
            'w-full',
            'after:absolute',
            'after:top-0',
            'after:left-0',
            'after:w-0',
            'after:h-0',
            'after:border-t-8',
            'after:border-t-green',
            'after:border-r-6',
            'after:border-r-transparent',
            'after:border-l-6',
            'after:border-l-transparent',
            'after:-translate-x-1/2',
            'after:-translate-y-0',
        );
        pixelOffset.appendChild(content);

        this.anchor = document.createElement('div');
        this.anchor.classList.add(
            'absolute',
            'w-72',
            'h-0',
        );
        this.anchor.appendChild(pixelOffset);

        // optionally stop clicks, etc., from bubbling up to the map
        this.stopEventPropagation();
    };

    // NOTE: google.maps.OverlayView is only defined once the Maps API has
    // loaded. That is why Popup is defined inside initMap()
    Popup.prototype = Object.create(google.maps.OverlayView.prototype);

    // called when the popup is added to the map
    Popup.prototype.onAdd = function () { // eslint-disable-line func-names
        this.getPanes().floatPane.appendChild(this.anchor);
    };

    // called when the popup is removed from the map
    Popup.prototype.onRemove = function () { // eslint-disable-line func-names
        if (this.anchor.parentElement) {
            this.anchor.parentElement.removeChild(this.anchor);
        }
    };

    // called when the popup needs to draw itself
    Popup.prototype.draw = function () { // eslint-disable-line func-names
        const divPosition = this.getProjection().fromLatLngToDivPixel(
            this.position,
        );
        // hide the popup when it is far out of view
        const display = Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000
            ? 'block'
            : 'none';

        if (display === 'block') {
            this.anchor.style.left = `${divPosition.x}px`;
            this.anchor.style.top = `${divPosition.y}px`;
        }

        if (this.anchor.style.display !== display) {
            this.anchor.style.display = display;
        }
    };

    // stops clicks/drags from bubbling up to the map
    Popup.prototype.stopEventPropagation = function () { // eslint-disable-line func-names
        const { anchor } = this;
        anchor.style.cursor = 'auto';

        [
            'click',
            'dblclick',
            'contextmenu',
            'wheel',
            'mousedown',
            'touchstart',
            'pointerdown',
        ].forEach((event) => {
            anchor.addEventListener(event, (e) => {
                e.stopPropagation();
            });
        });
    };
};

const triggerMarkerClick = (id) => {
    google.maps.event.trigger(markers.filter((marker) => marker.dealerInfo.dealerid === id)[0], 'click');
};

const removeMarkers = () => {
    if (markers.length === 0) return;

    for (let i = 0; i < markers.length; i += 1) {
        markers[i].setMap(null);
    }

    markers.length = 0;
};

const removePopups = () => {
    if (popups.length === 0) return;

    for (let i = 0; i < popups.length; i += 1) {
        popups[i].setMap(null);
    }

    popups.length = 0;
};

async function addMarkers(dealers) {
    // clear markers
    removeMarkers();

    // clear popups
    removePopups();

    dealers.forEach((dealer) => {
        // set dealer position
        const position = new google.maps.LatLng(
            dealer.marker.geometry.coordinates[1],
            dealer.marker.geometry.coordinates[0],
        );

        // build marker
        const marker = new google.maps.Marker({
            dealerInfo: dealer.marker.properties,
            map,
            position,
        });

        // build popup
        const content = document.createElement('div');
        content.classList.add('text-white');
        content.innerHTML = `
            <h2 class="max-w-xs mb-3.5 text-xl font-bold">${marker.dealerInfo.name}</h2>
            <p class="max-w-xs mb-3.5 text-base">${marker.dealerInfo.address}</p>
            ${marker.dealerInfo.phone ? `
                <a
                    href="tel:${marker.dealerInfo.phone}"
                    class="block max-w-xs mb-3.5 text-base"
                    target="_blank">${marker.dealerInfo.phone}</a>
            ` : ''}
            <a
                href="https://www.google.com/maps/dir/?api=1&destination=${position.lat()},${position.lng()}"
                class="block text-lime text-base font-bold"
                target="_blank">Directions <svg class="inline-block size-4 ml-2"><use xlink:href="#icon-directions" class="fill-lime"></use></svg></a>
        `;

        // &origin=${centre.lat()},${centre.lng()}
        const popup = new Popup(position, content);

        // handle marker click event
        marker.addListener('click', () => {
            // close all popups
            for (let i = 0; i < popups.length; i += 1) {
                popups[i].setMap(null);
            }

            // open popup
            popup.setMap(map);

            // center map
            map.setCenter(position);

            // highlight card
            const selectedCard = document.querySelector('.js-map-card.is-selected');
            if (selectedCard) {
                selectedCard.classList.remove('is-selected');
                // const selectedCardInner = selectedCard.querySelector('.js-map-card-inner');
                // selectedCardInner.classList.remove('border-orange');
            }

            const card = document.querySelector(`.js-map-card[data-id="${marker.dealerInfo.dealerid}"]`);
            if (card) {
                card.classList.add('is-selected');
                // const cardInner = card.querySelector('.js-map-card-inner');
                // cardInner.classList.add('!border-orange');
            }
        });

        // build markers array
        markers.push(marker);

        // build popups array
        popups.push(popup);
    });
}

async function calculateDistances(origin) {
    const dealers = [];
    const destinations = [];

    // build parallel arrays for the store IDs and destinations
    markers.forEach((marker) => {
        dealers.push(marker.dealerInfo);
        destinations.push(marker.position);
    });

    // retrieve the distances of each store from the origin
    // the returned list will be in the same order as the destinations list
    const service = new google.maps.DistanceMatrixService();

    const getDistanceMatrix = (serviceInstance, parameters) => new Promise((resolve, reject) => {
        serviceInstance.getDistanceMatrix(parameters, (response, status) => {
            if (status !== google.maps.DistanceMatrixStatus.OK) {
                reject(response);
            } else {
                const distances = [];
                const results = response.rows[0].elements;

                for (let j = 0; j < results.length; j += 1) {
                    const element = results[j];
                    const distanceObject = {
                        dealerInfo: dealers[j],
                        distanceText: element.distance.text,
                        distanceVal: element.distance.value,
                        location: destinations[j],
                    };
                    distances.push(distanceObject);
                }

                resolve(distances);
            }
        });
    });

    const distancesList = await getDistanceMatrix(service, {
        origins: [origin],
        destinations,
        travelMode: 'DRIVING',
        unitSystem: google.maps.UnitSystem.METRIC,
    });

    distancesList.sort((first, second) => first.distanceVal - second.distanceVal);

    return distancesList;
}

function showDealersList(data, dealers) {
    if (dealers.length === 0) {
        console.error('Empty dealers'); // eslint-disable-line no-console
        return;
    }

    // get map centre
    const centre = data.map.getCenter();

    dealers.forEach((dealer) => {
        const html = `
            <li
                class="grid gap-2.5 py-8 px-5 sm:px-10 border-b last:border-b-0 border-white/30 text-white text-sm hover:bg-green-mid/20 [&.is-selected]:bg-green-mid/20 js-map-card"
                role="button"
                tabindex="0"
                data-id="${dealer.dealerInfo.dealerid}">
                <h2 class="text-base font-bold uppercase">${dealer.dealerInfo.name}</h2>
                <div class="flex">
                    <strong class="pr-4 mr-4 border-r whitespace-nowrap">${dealer.distanceText}</strong>
                    <address>${dealer.dealerInfo.address}</address>
                </div>
                <div class="flex text-lime font-bold">
                    <a
                        href="https://www.google.com/maps/dir/?api=1&origin=${centre.lat()},${centre.lng()}&destination=${dealer.location.lat()},${dealer.location.lng()}"
                        class="hover:underline"
                        target="_blank">Directions<svg class="inline-block size-3 ml-2"><use xlink:href="#icon-directions" class="fill-lime"></use></svg></a>
                    ${dealer.dealerInfo.phone !== undefined && dealer.dealerInfo.phone !== '' ? `
                        <a
                            href="tel:${dealer.dealerInfo.phone}"
                            class="ml-auto hover:underline"
                            target="_blank"><svg class="inline-block size-3 mr-2"><use xlink:href="#icon-phone" class="fill-lime"></use></svg>Phone</a>
                    ` : ''}
                </div>
            </li>
        `;

        searchResults.insertAdjacentHTML('beforeend', html);
    });

    // attach click events to trigger marker click
    const dealerSearchResults = document.querySelectorAll('.js-map-card');
    dealerSearchResults.forEach((dealerSearchResult) => {
        dealerSearchResult.addEventListener('click', () => {
            triggerMarkerClick(Number(dealerSearchResult.dataset.id));
        });
    });

    // Open the search results
    searchResults.classList.add('open');
}

let autocomplete;

const findClosestDealers = (center) => {
    const data = new FormData();
    data.append('action', searchMap.dataset.action);
    data.append('nonce', searchMap.dataset.nonce);
    data.append('lat', center.lat());
    data.append('lng', center.lng());

    // Empty the search results
    while (searchResults.lastChild) {
        searchResults.removeChild(searchResults.lastChild);
    }

    fetch(SWDLocalise.ajaxUrl, {
        method: 'POST',
        cache: 'no-cache',
        body: data,
    })
        .then(handleResponse)
        .then((response) => {
            if (response.success) {
                (async () => {
                    await addMarkers(response.dealers);
                    const rankedDealers = await calculateDistances(center);
                    showDealersList(map.data, rankedDealers);
                    disableLoader();
                })();
                return;
            }

            // Add a message to the search results
            searchResults.insertAdjacentHTML('beforeend', '<li class="py-8 px-5 sm:px-10 text-white text-sm">No dealers found.</li>');

            disableLoader();
            console.error('No dealers found.'); // eslint-disable-line no-console
        })
        .catch((error) => {
            disableLoader();
            console.error(error); // eslint-disable-line no-console
        });
};

const getUserLoaction = (position) => (
    new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        const method = 'GET';
        const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${position.lat},${position.lng}&key=${key}`;
        const async = true;

        // must be sent over SSL
        request.open(method, url, async);
        request.onreadystatechange = () => {
            if (request.readyState === 4) {
                if (request.status === 200) {
                    const data = JSON.parse(request.responseText);
                    const address = data.results[0];
                    resolve(address);
                } else {
                    reject(request.status);
                }
            }
        };
        request.send();
    })
);

const geolocateUser = () => {
    enableLoader();

    navigator.geolocation.getCurrentPosition(async (position) => {
        const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
        };

        getUserLoaction(pos).then((response) => {
            if (response !== undefined) {
                searchInput.value = response.formatted_address;
            }
        }).catch(console.error);

        map.setCenter(pos);
        map.setZoom(14);

        // set marker
        const marker = new google.maps.Marker({ // eslint-disable-line no-unused-vars
            map,
            position: map.getCenter(),
            icon: svgMarker,
        });

        // use the selected address as the origin to calculate distances
        // to each of the dealer locations
        findClosestDealers(map.getCenter());
    });
};

const initGeolocate = () => {
    const geolocateBtn = document.querySelector('.js-map-geolocate-btn');
    if (navigator.geolocation) {
        geolocateBtn.addEventListener('click', () => {
            geolocateUser();
        });
    } else {
        geolocateBtn.style.display = 'none';
    }
};

const init = () => {
    // find a dealer
    if (searchMap) {
        loader
            .load()
            .then(() => {
                definePopupClass();

                svgMarker = {
                    path: 'M25,0C15.8,0,8.3,7.5,8.3,16.7c0,3,0.8,6,2.4,8.5L25,50l14.3-24.8c4.7-7.9,2.1-18.1-5.8-22.8 C30.9,0.8,28,0,25,0z M25,22.2c-3.1,0-5.6-2.5-5.6-5.6s2.5-5.6,5.6-5.6c3.1,0,5.6,2.5,5.6,5.6C30.6,19.7,28.1,22.2,25,22.2 C25,22.2,25,22.2,25,22.2z',
                    fillColor: '#023b3a',
                    fillOpacity: 1,
                    strokeWeight: 0,
                    rotation: 0,
                    anchor: new google.maps.Point(25, 50),
                };

                // check if center should be prefilled with user input
                const prefilledLat = getUrlParams().lat;
                const lat = Number(prefilledLat !== undefined && prefilledLat !== '' ? prefilledLat : 52.632469);
                const prefilledLng = getUrlParams().lng;
                const lng = Number(prefilledLng !== undefined && prefilledLng !== '' ? prefilledLng : -1.689423);

                // init map
                map = new google.maps.Map(searchMap, {
                    center: { lat, lng },
                    zoom: (lat !== 52.632469 && lng !== -1.689423 ? 13 : 7),
                });

                // check if address should be prefilled with user input
                const prefilledAddress = getUrlParams().address;
                if (prefilledAddress !== undefined) {
                    searchInput.value = decodeURIComponent(prefilledAddress.replace('+', ' '));
                }

                // get nearest dealers if center prefilled by user
                if (prefilledLat !== undefined
                    && prefilledLat !== ''
                    && prefilledLng !== undefined
                    && prefilledLng !== '') {
                    findClosestDealers(map.getCenter());
                }

                // init autocomplete
                autocomplete = new google.maps.places.Autocomplete(searchInput, {
                    componentRestrictions: { country: 'gb' },
                });

                // bind
                autocomplete.bindTo('bounds', map);

                // set the data fields to return when the user selects a place.
                autocomplete.setFields(['address_components', 'geometry', 'icon', 'name']);

                // Set the origin point when the user selects an address
                const originMarker = new google.maps.Marker({ map, icon: svgMarker });
                originMarker.setVisible(false);
                let originLocation = map.getCenter();

                function handleSelectedPlace(place) {
                    if (!place.geometry || !place.geometry.location) {
                        console.warn(`No address available for input: '${place.name}'`);
                        return;
                    }

                    originLocation = place.geometry.location;
                    map.setCenter(originLocation);
                    map.setZoom(12);

                    originMarker.setPosition(originLocation);
                    originMarker.setVisible(true);

                    findClosestDealers(originLocation);
                }

                const autocompleteService = new google.maps.places.AutocompleteService();
                const placesService = new google.maps.places.PlacesService(document.createElement('div'));

                searchInput.addEventListener('keydown', (e) => {
                    if (e.key !== 'Enter') return;

                    e.preventDefault();

                    const query = searchInput.value;
                    if (!query) return;

                    autocompleteService.getPlacePredictions(
                        { input: query },
                        (predictions, statusA) => {
                            if (statusA === google.maps.places.PlacesServiceStatus.OK
                                && predictions.length > 0) {
                                const firstPrediction = predictions[0];

                                // Now get place details
                                placesService.getDetails(
                                    { placeId: firstPrediction.place_id },
                                    (place, statusB) => {
                                        if (statusB === google.maps.places.PlacesServiceStatus.OK) {
                                            // ✅ Manually handle selection
                                            handleSelectedPlace(place);
                                        } else {
                                            console.warn('Failed to get place details:', statusB);
                                        }
                                    },
                                );
                            } else {
                                console.log('No predictions found');
                            }
                        },
                    );
                });

                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    handleSelectedPlace(place);
                });

                // geolocate
                initGeolocate();
            })
            .catch((error) => {
                console.error(error); // eslint-disable-line no-console
            });
    }
};

export default init;
